import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Tag, Typography, Space, Divider, Empty, Spin,
  Modal, Input, Popconfirm, Statistic, Row, Col, Badge, Alert,
  Tooltip, List, Avatar, Table, Progress, Drawer
} from 'antd';
import { App } from 'antd';
import {
  ThunderboltOutlined, CheckCircleOutlined, CloseCircleOutlined,
  EditOutlined, PlayCircleOutlined, UserOutlined, ReloadOutlined,
  RocketOutlined, TeamOutlined, MessageOutlined, BarChartOutlined,
  ClockCircleOutlined, SendOutlined, RobotOutlined, LoadingOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import apiClient from '../api';

const { TextArea } = Input;

// ── Standalone components (defined OUTSIDE CommandCentre to prevent remount) ──

const StatsRow = ({ stats }) => (
  <Row gutter={16} style={{ marginBottom: 24 }}>
    {[
      { title: 'Campaigns Sent', value: stats?.sent || 0,           icon: <RocketOutlined />,    color: '#52c41a' },
      { title: 'Users Reached',  value: stats?.totalSent || 0,      icon: <TeamOutlined />,      color: '#1890ff' },
      { title: 'Replied',        value: stats?.totalReplied || 0,   icon: <MessageOutlined />,   color: '#722ed1' },
      { title: 'Converted',      value: stats?.totalConverted || 0, icon: <BarChartOutlined />,  color: '#faad14' },
    ].map((s, i) => (
      <Col xs={12} sm={6} key={i}>
        <Card size="small" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 24, color: s.color, marginBottom: 4 }}>{s.icon}</div>
          <Statistic title={s.title} value={s.value} valueStyle={{ color: s.color, fontSize: 22 }} />
        </Card>
      </Col>
    ))}
  </Row>
);

const CampaignCard = ({ campaign, onApprove, onReject, onEdit, onDetail, showActions = true }) => (
  <Card
    style={{ marginBottom: 16, borderLeft: `4px solid ${campaign.status === 'pending' ? '#1890ff' : '#d9d9d9'}` }}
    size="small"
    title={
      <Space>
        <ThunderboltOutlined style={{ color: '#faad14' }} />
        <Typography.Text strong>{campaign.segmentLabel}</Typography.Text>
        <Tag color={STATUS_COLOR[campaign.status]}>{STATUS_LABEL[campaign.status]}</Tag>
        <Tag>{campaign.users.length} users</Tag>
      </Space>
    }
    extra={
      showActions && campaign.status === 'pending' ? (
        <Space>
          <Tooltip title="Edit message before sending">
            <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(campaign)}>Edit</Button>
          </Tooltip>
          <Popconfirm
            title="Send this campaign to all users in this segment?"
            onConfirm={() => onApprove(campaign._id)}
            okText="Yes, Send"
            cancelText="Not yet"
          >
            <Button size="small" type="primary" icon={<CheckCircleOutlined />}>Approve & Send</Button>
          </Popconfirm>
          <Popconfirm
            title="Reject this campaign?"
            onConfirm={() => onReject(campaign._id)}
            okText="Reject"
            okType="danger"
          >
            <Button size="small" danger icon={<CloseCircleOutlined />}>Reject</Button>
          </Popconfirm>
        </Space>
      ) : campaign.status === 'sent' ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Sent {new Date(campaign.sentAt).toLocaleString()}
        </Typography.Text>
      ) : null
    }
  >
    <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
      💡 {campaign.reasoning}
    </Typography.Paragraph>

    {campaign.opportunity && (
      <Alert
        message={`📈 Opportunity: ${campaign.opportunity}`}
        type="info"
        style={{ marginBottom: 10, fontSize: 12 }}
        showIcon={false}
        banner
      />
    )}

    <div style={{ background: '#f6f8fa', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
      <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
        MESSAGE PREVIEW
      </Typography.Text>
      <Typography.Text style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{campaign.message}</Typography.Text>
    </div>

    <Space style={{ cursor: 'pointer' }} onClick={() => onDetail(campaign)}>
      {campaign.users.slice(0, 5).map((u, i) => (
        <Avatar key={i} size="small" style={{ background: '#1890ff', fontSize: 11 }}>
          {(u.name || 'U').charAt(0).toUpperCase()}
        </Avatar>
      ))}
      {campaign.users.length > 5 && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>+{campaign.users.length - 5} more</Typography.Text>
      )}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        <UserOutlined /> View all {campaign.users.length} users →
      </Typography.Text>
    </Space>

    {campaign.status === 'sent' && campaign.results && (
      <div style={{ marginTop: 10 }}>
        <Divider style={{ margin: '8px 0' }} />
        <Space split={<Divider type="vertical" />}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>📤 Sent: {campaign.results.sent}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>💬 Replied: {campaign.results.replied}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>✅ Converted: {campaign.results.converted}</Typography.Text>
        </Space>
      </div>
    )}
  </Card>
);
const { Title, Text, Paragraph } = Typography;

const STATUS_COLOR = {
  pending:  'processing',
  approved: 'success',
  rejected: 'default',
  sent:     'success',
  partial:  'warning',
};

const STATUS_LABEL = {
  pending:  '⏳ Pending Approval',
  approved: '✅ Approved',
  rejected: '❌ Rejected',
  sent:     '🚀 Sent',
  partial:  '⚠️ Partial Send',
};

const CommandCentre = () => {
  const { message } = App.useApp();
  const [loading, setLoading]           = useState(false);
  const [running, setRunning]           = useState(false);
  const [pending, setPending]           = useState([]);
  const [history, setHistory]           = useState([]);
  const [stats, setStats]               = useState(null);
  const [editCampaign, setEditCampaign] = useState(null);
  const [editMsg, setEditMsg]           = useState('');
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [userFilter, setUserFilter]     = useState('all');

  // Growth AI Chat
  const [showChat, setShowChat]       = useState(false);
  const [chatInput, setChatInput]     = useState('');
  const [chatMessages, setChatMessages] = useState([
    { role: 'ai', content: 'Hi! I am your Growth Assistant. I can analyze your sales funnel, find specific leads, or even draft a campaign for you. What would you like to do today?' }
  ]);
  const [chatLoading, setChatLoading] = useState(false);

  const sendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg = { role: 'human', content: chatInput };
    setChatMessages(prev => [...prev, userMsg]);
    const currentInput = chatInput;
    setChatInput('');
    setChatLoading(true);
    try {
      const res = await apiClient.post('/command-centre/chat', { 
        message: currentInput,
        history: chatMessages.slice(-10) // Send last 10 messages for context
      });
      if (res.data.success) {
        setChatMessages(prev => [...prev, { role: 'ai', content: res.data.response.content }]);
      }
    } catch (err) {
      message.error('Assistant failed to reply');
    } finally {
      setChatLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [campRes, statsRes] = await Promise.all([
        apiClient.get('/command-centre/campaigns'),
        apiClient.get('/command-centre/stats'),
      ]);
      if (campRes.data.success)  { setPending(campRes.data.pending); setHistory(campRes.data.history); }
      if (statsRes.data.success) setStats(statsRes.data.stats);
    } catch (err) {
      message.error('Failed to load Command Centre');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runJob = async () => {
    setRunning(true);
    try {
      await apiClient.post('/command-centre/run');
      message.success('Analysis started! Checking for results every 15s…');
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const [campRes, statsRes] = await Promise.all([
            apiClient.get('/command-centre/campaigns'),
            apiClient.get('/command-centre/stats'),
          ]);
          if (campRes.data.success) {
            setPending(campRes.data.pending);
            setHistory(campRes.data.history);
          }
          if (statsRes.data.success) setStats(statsRes.data.stats);

          const hasPending = campRes.data.pending?.length > 0;
          if (hasPending || attempts >= 12) {   // found results OR 3 min timeout
            clearInterval(poll);
            setRunning(false);
            if (hasPending) message.success(`Analysis complete — ${campRes.data.pending.length} campaigns ready`);
          }
        } catch {
          // keep polling on transient error
        }
      }, 15000);
    } catch {
      message.error('Failed to start analysis');
      setRunning(false);
    }
  };

  const approveCampaign = async (id) => {
    try {
      const res = await apiClient.patch(`/command-centre/campaigns/${id}/approve`);
      if (res.data.success) {
        message.success(res.data.message);
        load();
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Approval failed');
    }
  };

  const rejectCampaign = async (id) => {
    try {
      await apiClient.patch(`/command-centre/campaigns/${id}/reject`);
      message.info('Campaign rejected');
      load();
    } catch {
      message.error('Reject failed');
    }
  };

  const saveMessage = async () => {
    if (!editMsg.trim()) return;
    try {
      await apiClient.patch(`/command-centre/campaigns/${editCampaign._id}/message`, {
        message: editMsg.trim(),
      });
      message.success('Message updated');
      setEditCampaign(null);
      load();
    } catch {
      message.error('Failed to update message');
    }
  };

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>🎯 Command Centre</Title>
          <Text type="secondary">
            AI analyses your conversations every night and suggests who to re-engage, with what message.
            You approve. Engine sends.
          </Text>
        </div>
        <Space>
          <Button
            type="primary"
            ghost
            icon={<RobotOutlined />}
            onClick={() => setShowChat(true)}
            size="large"
            style={{ 
              borderColor: '#faad14', 
              color: '#faad14', 
              boxShadow: '0 0 8px rgba(250,173,20,0.2)',
              fontWeight: 600
            }}
          >
            Meet your Growth Ally
          </Button>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            loading={running}
            onClick={runJob}
            size="large"
          >
            {running ? 'Analysing…' : 'Run Analysis Now'}
          </Button>
        </Space>
      </div>

      {/* Stats */}
      {stats && <StatsRow stats={stats} />}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading campaigns…</Text>
          </div>
        </div>
      ) : (
        <>
          {/* Tonight's pending campaigns */}
          <Card
            title={
              <Space>
                <Badge count={pending.length} showZero>
                  <ClockCircleOutlined style={{ fontSize: 16 }} />
                </Badge>
                <Text strong>Tonight's Suggested Campaigns</Text>
                <Button size="small" icon={<ReloadOutlined />} onClick={load} />
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            {pending.length === 0 ? (
              <Empty
                description={
                  <span>
                    No campaigns yet.{' '}
                    <Button type="link" onClick={runJob} loading={running} style={{ padding: 0 }}>
                      Run Analysis
                    </Button>{' '}
                    to discover segments.
                  </span>
                }
              />
            ) : (
              pending.map(c => <CampaignCard key={c._id} campaign={c} showActions
                onApprove={approveCampaign} onReject={rejectCampaign}
                onEdit={c => { setEditCampaign(c); setEditMsg(c.message); }}
                onDetail={setDetailCampaign}
              />)
            )}
          </Card>

          {/* Campaign Results History */}
          {history.length > 0 && (
            <Card
              title={
                <Space>
                  <BarChartOutlined style={{ color: '#1890ff' }} />
                  <Typography.Text strong>Campaign Results</Typography.Text>
                  <Tag>{history.length} campaigns</Tag>
                </Space>
              }
            >
              <Table
                dataSource={history}
                rowKey="_id"
                size="small"
                pagination={{ pageSize: 10, showSizeChanger: false }}
                expandable={{
                  expandedRowRender: c => (
                    <div style={{ padding: '8px 0' }}>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        💡 {c.reasoning}
                      </Typography.Text>
                      <div style={{ background: '#f6f8fa', borderRadius: 6, padding: '8px 12px', marginTop: 8 }}>
                        <Typography.Text style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                          {c.message}
                        </Typography.Text>
                      </div>
                      {c.opportunity && (
                        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                          📈 {c.opportunity}
                        </Typography.Text>
                      )}
                    </div>
                  ),
                  rowExpandable: () => true,
                }}
                columns={[
                  {
                    title: 'Date',
                    dataIndex: 'sentAt',
                    width: 110,
                    render: d => d
                      ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                      : new Date(d || 0).toLocaleDateString(),
                    sorter: (a, b) => new Date(a.sentAt) - new Date(b.sentAt),
                    defaultSortOrder: 'descend',
                  },
                  {
                    title: 'Segment',
                    dataIndex: 'segmentLabel',
                    render: (label, c) => (
                      <Space direction="vertical" size={0}>
                        <Typography.Text strong style={{ fontSize: 13 }}>{label}</Typography.Text>
                        <Tag style={{ fontSize: 10, marginTop: 2 }}>{c.status}</Tag>
                      </Space>
                    ),
                  },
                  {
                    title: 'Sent',
                    width: 60,
                    render: c => (
                      <Typography.Text strong>{c.results?.sent || 0}</Typography.Text>
                    ),
                  },
                  {
                    title: 'Replied',
                    width: 120,
                    render: c => {
                      const sent    = c.results?.sent || 0;
                      const replied = c.results?.replied || 0;
                      const pct     = sent > 0 ? Math.round((replied / sent) * 100) : 0;
                      return (
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Typography.Text style={{ fontSize: 12 }}>{replied} <Typography.Text type="secondary" style={{ fontSize: 11 }}>({pct}%)</Typography.Text></Typography.Text>
                          <Progress percent={pct} size="small" showInfo={false}
                            strokeColor={pct >= 30 ? '#52c41a' : pct >= 10 ? '#faad14' : '#ff4d4f'}
                          />
                        </Space>
                      );
                    },
                  },
                  {
                    title: 'Converted',
                    width: 120,
                    render: c => {
                      const sent      = c.results?.sent || 0;
                      const converted = c.results?.converted || 0;
                      const pct       = sent > 0 ? Math.round((converted / sent) * 100) : 0;
                      return (
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Typography.Text style={{ fontSize: 12 }}>{converted} <Typography.Text type="secondary" style={{ fontSize: 11 }}>({pct}%)</Typography.Text></Typography.Text>
                          <Progress percent={pct} size="small" showInfo={false} strokeColor="#722ed1" />
                        </Space>
                      );
                    },
                  },
                  {
                    title: 'Users',
                    width: 80,
                    render: c => (
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={() => setDetailCampaign(c)}
                      >
                        {c.users?.length || 0} users
                      </Button>
                    ),
                  },
                ]}
              />
            </Card>
          )}
        </>
      )}

      {/* Edit message modal */}
      <Modal
        title={<Space><EditOutlined /> Edit Campaign Message</Space>}
        open={!!editCampaign}
        onCancel={() => setEditCampaign(null)}
        onOk={saveMessage}
        okText={<Space><SendOutlined /> Save & Ready to Send</Space>}
        width={540}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            message={`Segment: ${editCampaign?.segmentLabel} — ${editCampaign?.users.length} users`}
            type="info"
            showIcon={false}
            banner
          />
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
              This message goes to all users in the segment. Be warm, specific, and include one CTA.
            </Text>
            <TextArea
              rows={5}
              value={editMsg}
              onChange={e => setEditMsg(e.target.value)}
              placeholder="Write your re-engagement message…"
              showCount
              maxLength={500}
            />
          </div>
        </Space>
      </Modal>

      {/* Campaign Detail Modal */}
      <Modal
        title={
          <Space>
            <ThunderboltOutlined style={{ color: '#faad14' }} />
            <span>{detailCampaign?.segmentLabel}</span>
            <Tag color={STATUS_COLOR[detailCampaign?.status]}>{STATUS_LABEL[detailCampaign?.status]}</Tag>
          </Space>
        }
        open={!!detailCampaign}
        onCancel={() => { setDetailCampaign(null); setUserFilter('all'); }}
        footer={null}
        width={760}
        styles={{ body: { padding: '16px 24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        {detailCampaign && (() => {
          const users  = detailCampaign.users || [];
          const isSent = detailCampaign.status === 'sent';
          const sent      = detailCampaign.results?.sent || 0;
          const replied   = detailCampaign.results?.replied || 0;
          const converted = detailCampaign.results?.converted || 0;
          const replyPct  = sent > 0 ? Math.round((replied / sent) * 100) : 0;
          const convPct   = sent > 0 ? Math.round((converted / sent) * 100) : 0;

          const filtered =
            userFilter === 'replied'    ? users.filter(u => u.replied) :
            userFilter === 'converted'  ? users.filter(u => u.converted) :
            userFilter === 'no_reply'   ? users.filter(u => u.sent && !u.replied) :
            users;

          return (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>

              {/* ── Campaign summary ── */}
              <Card size="small" style={{ background: '#fafafa', border: '1px solid #e8e8e8' }}>
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                  {detailCampaign.reasoning && (
                    <Text type="secondary" style={{ fontSize: 13 }}>💡 {detailCampaign.reasoning}</Text>
                  )}
                  {detailCampaign.opportunity && (
                    <Alert
                      message={`📈 ${detailCampaign.opportunity}`}
                      type="info"
                      banner
                      showIcon={false}
                      style={{ fontSize: 12 }}
                    />
                  )}
                  <div style={{ background: '#fff', borderRadius: 6, padding: '8px 12px', borderLeft: '3px solid #1890ff' }}>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>MESSAGE</Text>
                    <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{detailCampaign.message}</Text>
                  </div>
                </Space>
              </Card>

              {/* ── Results bar (only for sent campaigns) ── */}
              {isSent && (
                <Row gutter={12}>
                  {[
                    { label: 'Sent',      value: sent,      pct: null,     color: '#1890ff' },
                    { label: 'Replied',   value: replied,   pct: replyPct, color: '#722ed1' },
                    { label: 'Converted', value: converted, pct: convPct,  color: '#52c41a' },
                  ].map(s => (
                    <Col span={8} key={s.label}>
                      <Card size="small" style={{ textAlign: 'center' }}>
                        <Statistic
                          title={s.label}
                          value={s.pct !== null ? `${s.value} (${s.pct}%)` : s.value}
                          valueStyle={{ color: s.color, fontSize: 18 }}
                        />
                        {s.pct !== null && (
                          <Progress percent={s.pct} size="small" showInfo={false} strokeColor={s.color} style={{ marginTop: 4 }} />
                        )}
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}

              {/* ── User list ── */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <Text strong>{isSent ? 'User Engagement' : 'Users in Segment'} ({users.length})</Text>
                  {isSent && (
                    <Space size={6}>
                      {[
                        { key: 'all',       label: `All (${users.length})` },
                        { key: 'replied',   label: `💬 ${users.filter(u => u.replied).length}` },
                        { key: 'converted', label: `✅ ${users.filter(u => u.converted).length}` },
                        { key: 'no_reply',  label: `⏳ ${users.filter(u => u.sent && !u.replied).length}` },
                      ].map(f => (
                        <Button
                          key={f.key}
                          size="small"
                          type={userFilter === f.key ? 'primary' : 'default'}
                          onClick={() => setUserFilter(f.key)}
                        >
                          {f.label}
                        </Button>
                      ))}
                    </Space>
                  )}
                </div>

                <List
                  dataSource={filtered}
                  locale={{ emptyText: 'No users in this filter' }}
                  renderItem={user => (
                    <List.Item style={{ alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <List.Item.Meta
                        avatar={
                          <Avatar style={{
                            background: user.converted ? '#52c41a' : user.replied ? '#722ed1' : user.sent ? '#1890ff' : '#bfbfbf',
                            flexShrink: 0,
                            fontSize: 14,
                          }}>
                            {(user.name || 'U').charAt(0).toUpperCase()}
                          </Avatar>
                        }
                        title={
                          <Space wrap size={4}>
                            <Text strong style={{ fontSize: 13 }}>{user.name || 'Unknown'}</Text>
                            {user.phone && <Text type="secondary" style={{ fontSize: 11 }}>+{user.phone}</Text>}
                            <span>
                              {user.converted && <Tag color="success" style={{ fontSize: 10, marginLeft: 4 }}>✅ Converted</Tag>}
                              {user.replied   && !user.converted && <Tag color="purple" style={{ fontSize: 10, marginLeft: 4 }}>💬 Replied</Tag>}
                              {user.sent      && !user.replied   && <Tag color="blue"   style={{ fontSize: 10, marginLeft: 4 }}>📤 Sent</Tag>}
                              {!user.sent     && <Tag color="default" style={{ fontSize: 10, marginLeft: 4 }}>Pending</Tag>}
                              {user.sent && !user.replied && <Tag color="default" style={{ fontSize: 10 }}>No reply yet</Tag>}
                            </span>
                          </Space>
                        }
                        description={
                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            {user.conversationSummary && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {user.conversationSummary}
                              </Text>
                            )}
                            {user.personalizedMessage && (
                              <div style={{ background: '#f6f8fa', borderRadius: 6, padding: '5px 10px', borderLeft: '3px solid #1890ff' }}>
                                <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap', color: '#444' }}>
                                  {user.personalizedMessage}
                                </Text>
                              </div>
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            </Space>
          );
        })()}
      </Modal>

      {/* Growth AI Assistant Drawer */}
      <Drawer
        title={<Space><RobotOutlined /> Growth AI Assistant</Space>}
        placement="right"
        width={400}
        onClose={() => setShowChat(false)}
        open={showChat}
        styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
      >
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f5f5f5' }}>
          {chatMessages.map((msg, i) => (
            <div key={i} style={{ 
              marginBottom: 16, 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: msg.role === 'human' ? 'flex-end' : 'flex-start'
            }}>
              <div style={{ 
                maxWidth: '85%', 
                padding: '10px 14px', 
                borderRadius: 12, 
                fontSize: 13,
                background: msg.role === 'human' ? '#1890ff' : '#fff',
                color: msg.role === 'human' ? '#fff' : '#000',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <ReactMarkdown 
                  components={{
                    h3: ({node, ...props}) => <Text strong style={{ display: 'block', fontSize: 14, margin: '8px 0 4px 0' }} {...props} />,
                    p: ({node, ...props}) => <p style={{ margin: '0 0 8px 0' }} {...props} />,
                    ul: ({node, ...props}) => <ul style={{ paddingLeft: 18, marginBottom: 8 }} {...props} />,
                    li: ({node, ...props}) => <li style={{ marginBottom: 4 }} {...props} />,
                  }}
                >
                  {msg.content}
                </ReactMarkdown>
              </div>
              <Text type="secondary" style={{ fontSize: 10, marginTop: 4 }}>
                {msg.role === 'human' ? 'You' : 'Growth AI'}
              </Text>
            </div>
          ))}
          {chatLoading && (
            <div style={{ textAlign: 'left', marginBottom: 16 }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />
            </div>
          )}
        </div>
        <div style={{ padding: 16, borderTop: '1px solid #f0f0f0', background: '#fff' }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input 
              placeholder="Ask for analysis or a campaign..." 
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onPressEnter={sendChat}
              disabled={chatLoading}
            />
            <Button type="primary" icon={<SendOutlined />} onClick={sendChat} loading={chatLoading} />
          </Space.Compact>
        </div>
      </Drawer>
    </div>

  );
};

export default CommandCentre;
